/**
 * Public-only stub providers for platform-public.
 * No user is ever logged in on the public site; these provide the same context
 * shapes as the real Auth/Profile/UserType/Tenant providers but with no MSAL,
 * no API calls, and fixed "unauthenticated" state. Use these instead of the
 * real providers to avoid pulling auth/tenant/profile logic into the bundle.
 */

import React, { useState, useCallback, useMemo } from 'react';
import AuthContext from './AuthContext';
import { ProfileContext } from '../utils/profileContext';
import { UserTypeContext, USER_TYPES, VIEW_MODES } from '../utils/userTypeContext';
import { TenantContext } from './TenantContext';

// --- Auth (no MSAL, no tokens) ---
const PUBLIC_AUTH_VALUE = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  getAccessToken: async () => null,
  login: async () => {},
  logout: async () => {},
  instance: null
};

export function PublicAuthProvider({ children }) {
  return (
    <AuthContext.Provider value={PUBLIC_AUTH_VALUE}>
      {children}
    </AuthContext.Provider>
  );
}

// --- Profile (empty profile, no API) ---
const EMPTY_PROFILE = {
  displayName: '',
  email: '',
  username: '',
  accountStatus: 'Active',
  lastLogin: null,
  givenName: '',
  surname: '',
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  mobilePhone: '',
  businessPhone: ''
};

const PUBLIC_PROFILE_VALUE = {
  profile: EMPTY_PROFILE,
  isLoading: false,
  updateProfile: () => {},
  getDisplayInfo: () => ({ name: 'User', initials: 'U', email: '' })
};

export function PublicProfileProvider({ children }) {
  return (
    <ProfileContext.Provider value={PUBLIC_PROFILE_VALUE}>
      {children}
    </ProfileContext.Provider>
  );
}

// --- User type (stateful for public site, no API) ---
export function PublicUserTypeProvider({ children }) {
  const [userType, setUserTypeRaw] = useState(() => {
    const saved = localStorage.getItem('userType');
    return saved && Object.values(USER_TYPES).includes(saved) ? saved : USER_TYPES.CUSTOMER;
  });
  const [viewMode, setViewModeRaw] = useState(() => {
    const saved = localStorage.getItem('viewMode');
    return saved && Object.values(VIEW_MODES).includes(saved) ? saved : VIEW_MODES.NORMAL;
  });

  const setUserType = useCallback((newUserType) => {
    if (Object.values(USER_TYPES).includes(newUserType)) {
      setUserTypeRaw(newUserType);
      localStorage.setItem('userType', newUserType);
    }
  }, []);

  const setViewMode = useCallback((newViewMode) => {
    if (Object.values(VIEW_MODES).includes(newViewMode)) {
      setViewModeRaw(newViewMode);
      localStorage.setItem('viewMode', newViewMode);
    }
  }, []);

  const value = useMemo(() => ({
    userType,
    setUserType,
    viewMode,
    setViewMode,
    userDocumentType: null,
    isEndUser: false,
    isFullUser: true,
    isLoading: false,
    isPartner: userType === USER_TYPES.PARTNER,
    isCustomer: userType === USER_TYPES.CUSTOMER,
    isInvestorsRegulators: userType === USER_TYPES.INVESTORS_REGULATORS,
    isTechnical: viewMode === VIEW_MODES.TECHNICAL,
    isNormal: viewMode === VIEW_MODES.NORMAL
  }), [userType, viewMode, setUserType, setViewMode]);

  return (
    <UserTypeContext.Provider value={value}>
      {children}
    </UserTypeContext.Provider>
  );
}

// --- Tenant (no workspaces, no API) ---
const noop = () => {};
const asyncEmpty = async () => ({ tenants: [], tenantDetails: {}, permissions: {} });

const PUBLIC_TENANT_VALUE = {
  tenants: [],
  tenantDetails: {},
  permissions: {},
  loading: false,
  error: null,
  currentTenant: null,
  isMainTenantPartner: false,
  autoProvisioned: false,
  setAutoProvisioned: noop,
  loadTenantContext: asyncEmpty,
  refreshTenantContext: async () => {},
  getTenantById: () => null,
  getTenantName: () => null,
  getTenantPermissions: () => [],
  hasPermission: () => false,
  isCacheValid: false
};

export function PublicTenantProvider({ children }) {
  return (
    <TenantContext.Provider value={PUBLIC_TENANT_VALUE}>
      {children}
    </TenantContext.Provider>
  );
}
